// stores/authstore.ts
import { defineStore } from 'pinia'
import { auth, db } from '@/firebase'
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import type { User } from '@/types/auth'
import { clearPendingInvoice } from '@/utils/pendingInvoice'

export const useAuthStore = defineStore('authstore', {
    state: () => ({
        user: null as User | null,
    }),

    actions: {
        async signup(name: string, email: string, password: string) {
            try {
                const cred = await createUserWithEmailAndPassword(auth, email, password)
                if (cred.user) {
                    await updateProfile(cred.user, { displayName: name })
                    const userDoc = doc(db, 'users', cred.user.uid)
                    const profile = {
                        uid: cred.user.uid,
                        name,
                        email,
                        hasAccess: false,
                    }
                    await setDoc(userDoc, profile)
                    this.user = profile
                }
            } catch (err) {
                console.error('Signup failed:', err)
                throw err
            }
        },
        async login(email: string, password: string) {
            try {
                const userCredential = await signInWithEmailAndPassword(auth, email, password)
                const userAuth = userCredential.user

                const userData = await this.fetchUser(userAuth.uid)
                this.user = userData
            } catch (err) {
                console.error('Login failed:', err)
                throw err
            }
        },

        async restoreUser() {
            const currentUser = auth.currentUser
            if (!currentUser) return

            const userData = await this.fetchUser(currentUser.uid)
            this.user = userData
        },

        async fetchUser(uid: string): Promise<User | null> {
            const docSnap = await getDoc(doc(db, 'users', uid))
            return docSnap.exists() ? (docSnap.data() as User) : null
        },

        async logout() {
            await auth.signOut()
            this.user = null
            clearPendingInvoice()
            try {
                const [{ useInvoiceStore }, { useAppStore }, { useSettingsStore }] = await Promise.all([
                    import('@/store/invoicestore'),
                    import('@/store/app'),
                    import('@/store/settings'),
                ])

                const invoiceStore = useInvoiceStore()
                if (invoiceStore?.$reset) invoiceStore.$reset()

                const appStore = useAppStore()
                if (appStore?.$reset) appStore.$reset()

                const settingsStore = useSettingsStore()
                if (settingsStore?.$reset) settingsStore.$reset()
            } catch (e) {
                console.warn('Could not reset all stores on logout', e)
            }
        }

    }
})
